import { GetChargingSessionsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/emsp-bo/models/inputs/charging-sessions/get-charging-sessions-paginated.model';
import { useUrlSyncedFilters } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/hooks';
import { ChargingSessionModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/transaction-service-lib/src/wrapper/outputs/charging-session.model';
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
} from 'react';
import { DeepPartialSkipArrayKey } from 'react-hook-form';

import {
  TABLE_LIMITS_OPTIONS,
  TABLE_LIMIT_OPTION_MIN,
} from '@app/config/app.config';
import {
  EMSP_BO_CHARGING_SESSION_FILTERS_LSK,
  EMSP_BO_CHARGING_SESSION_LIST_OPTIONS_LSK,
} from '@app/config/localstorage-keys.const';
import { useLocalStorageOptions } from '@shared/hooks/useLocalStorageOptions';

import {
  EMSP_BO_CHARGING_SESSION_LIST_DEFAULT_OPTIONS,
  EMSP_BO_CHARGING_SESSION_LIST_SORT_FIELD,
  EMSP_BO_CHARGING_SESSION_LIST_SORT_ORDER,
  EmspBoChargingSessionListOptionsModel,
} from './emsp-bo-charging-session-list/emsp-bo-charging-session-list.config';

// /////////////////////////////////////////////////////////////////////////////

export const EmspBoChargingSessionContext =
  createContext<ChargingSessionModel | null>(null);

export const useEmspBoChargingSessionContext = (): ChargingSessionModel => {
  const context = useContext(EmspBoChargingSessionContext);

  if (!context) {
    throw new Error(
      'useEmspBoChargingSessionContext must be used within EmspBoChargingSessionContext.Provider',
    );
  }

  return context;
};

interface EmspBoChargingSessionContextProviderProps {
  data: ChargingSessionModel;
  children: React.ReactNode;
}

export const EmspBoChargingSessionContextProvider = ({
  data,
  children,
}: EmspBoChargingSessionContextProviderProps): React.JSX.Element => {
  return (
    <EmspBoChargingSessionContext.Provider value={data}>
      {children}
    </EmspBoChargingSessionContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export interface EmspBoChargingSessionListContextProps {
  getPaginatedQueryParameters: GetChargingSessionsPaginatedModel;
  setGetPaginatedQueryParameters: Dispatch<
    SetStateAction<GetChargingSessionsPaginatedModel>
  >;
  getPaginatedLimitList: number[];
  columnOptions: EmspBoChargingSessionListOptionsModel;
  setColumnOptions: Dispatch<
    SetStateAction<EmspBoChargingSessionListOptionsModel>
  >;
}

export const EmspBoChargingSessionListContext =
  createContext<EmspBoChargingSessionListContextProps | null>(null);

export const useEmspBoChargingSessionListContext =
  (): EmspBoChargingSessionListContextProps => {
    const context = useContext(EmspBoChargingSessionListContext);

    if (!context) {
      throw new Error(
        'useEmspBoChargingSessionListContext must be used within EmspBoChargingSessionListContext.Provider',
      );
    }

    return context;
  };

interface EmspBoChargingSessionListContextProviderProps {
  children: React.ReactNode;
}

export const EmspBoChargingSessionListContextProvider = ({
  children,
}: EmspBoChargingSessionListContextProviderProps): React.JSX.Element => {
  const [getPaginatedQueryParameters, setGetPaginatedQueryParameters] =
    useUrlSyncedFilters<GetChargingSessionsPaginatedModel>(
      {
        // Pagination
        limit: TABLE_LIMITS_OPTIONS.at(-1) ?? TABLE_LIMIT_OPTION_MIN,
        offset: 0,
        // Filterable fields
        textToSearch: '',
        // Sortable fields
        sortField: EMSP_BO_CHARGING_SESSION_LIST_SORT_FIELD,
        sortOrder: EMSP_BO_CHARGING_SESSION_LIST_SORT_ORDER,
      },
      ['limit', 'offset', 'sortField', 'sortOrder', 'textToSearch'],
      EMSP_BO_CHARGING_SESSION_FILTERS_LSK,
    );

  const [columnOptions, setColumnOptions] =
    useLocalStorageOptions<EmspBoChargingSessionListOptionsModel>(
      EMSP_BO_CHARGING_SESSION_LIST_DEFAULT_OPTIONS,
      EMSP_BO_CHARGING_SESSION_LIST_OPTIONS_LSK,
    );

  const data = useMemo(
    () => ({
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList: TABLE_LIMITS_OPTIONS,
      columnOptions,
      setColumnOptions,
    }),
    [
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      columnOptions,
      setColumnOptions,
    ],
  );

  return (
    <EmspBoChargingSessionListContext.Provider value={data}>
      {children}
    </EmspBoChargingSessionListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterEmspBoChargingSessionListContext =
  createContext<DeepPartialSkipArrayKey<GetChargingSessionsPaginatedModel> | null>(
    null,
  );

export const useFilterEmspBoChargingSessionListContext =
  (): DeepPartialSkipArrayKey<GetChargingSessionsPaginatedModel> => {
    const context = useContext(FilterEmspBoChargingSessionListContext);

    if (!context) {
      throw new Error(
        'useFilterEmspBoChargingSessionListContext must be used within FilterEmspBoChargingSessionListContext.Provider',
      );
    }

    return context;
  };

interface FilterEmspBoChargingSessionListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetChargingSessionsPaginatedModel>;
  children: React.ReactNode;
}

export const FilterEmspBoChargingSessionListContextProvider = ({
  data,
  children,
}: FilterEmspBoChargingSessionListContextProviderProps): React.JSX.Element => {
  return (
    <FilterEmspBoChargingSessionListContext.Provider value={data}>
      {children}
    </FilterEmspBoChargingSessionListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterSearchEmspBoChargingSessionListContext =
  createContext<DeepPartialSkipArrayKey<GetChargingSessionsPaginatedModel> | null>(
    null,
  );

export const useFilterSearchEmspBoChargingSessionListContext =
  (): DeepPartialSkipArrayKey<GetChargingSessionsPaginatedModel> => {
    const context = useContext(FilterSearchEmspBoChargingSessionListContext);

    if (!context) {
      throw new Error(
        'useFilterSearchEmspBoChargingSessionListContext must be used within FilterSearchEmspBoChargingSessionListContextProvider',
      );
    }

    return context;
  };

interface FilterSearchEmspBoChargingSessionListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetChargingSessionsPaginatedModel>;
  children: React.ReactNode;
}

export const FilterSearchEmspBoChargingSessionListContextProvider = ({
  data,
  children,
}: FilterSearchEmspBoChargingSessionListContextProviderProps): React.JSX.Element => {
  return (
    <FilterSearchEmspBoChargingSessionListContext.Provider value={data}>
      {children}
    </FilterSearchEmspBoChargingSessionListContext.Provider>
  );
};
